import { create } from 'zustand';
import { Intent, Plan, PlanStep, Column, DataType } from '../types';
import { aiApi, type BuildProgress, type BuildStreamContext } from '../services/api';
import { useWorkflowStore, FlowNode, FlowEdge } from './workflowStore';
import { useUploadedFileStore, UploadedFileData } from './uploadedFileStore';

// 生成唯一节点 ID
let nodeIdCounter = 0;
function generateNodeId(type: string): string {
  nodeIdCounter += 1;
  return `node_${type}_${Date.now()}_${nodeIdCounter}`;
}

// 重置计数器
export function resetNodeIdCounter() {
  nodeIdCounter = 0;
}

const STEP_LABELS: Record<string, string> = {
  load_excel: '加载数据',
  load_csv: '加载数据',
  load_table: '加载数据',
  load_api: 'API 数据',
  online_sheet: '读取在线表格',
  filter: '筛选数据',
  group_by: '分组',
  aggregate: '聚合统计',
  sort: '排序数据',
  compute: '计算列',
  transform: '映射转换',
  dedup: '去重',
  drop: '删除列/行',
  top_n: '取行数据',
  pivot: '数据透视',
  join: '合并数据',
  append: '追加',
  diff: '差异对比',
  barcode: '生成条码',
  data_normalization: '结构修复',
  export: '导出结果',
};

/** 按 depends 做拓扑排序，使预览顺序与数据流一致（导出在后），避免 steps 数组顺序与 DAG 不一致时错位 */
function sortPlanStepsForPipeline(steps: PlanStep[]): PlanStep[] {
  if (steps.length <= 1) return [...steps];
  const ids = new Set(steps.map((s) => s.id));
  const inDegree = new Map<string, number>();
  const outgoing = new Map<string, string[]>();

  for (const s of steps) {
    inDegree.set(s.id, 0);
  }
  for (const s of steps) {
    for (const d of s.depends || []) {
      if (!ids.has(d)) continue;
      inDegree.set(s.id, (inDegree.get(s.id) || 0) + 1);
      if (!outgoing.has(d)) outgoing.set(d, []);
      outgoing.get(d)!.push(s.id);
    }
  }

  const queue = steps
    .filter((s) => (inDegree.get(s.id) || 0) === 0)
    .map((s) => s.id)
    .sort((a, b) => a.localeCompare(b));

  const ordered: PlanStep[] = [];
  const seen = new Set<string>();

  while (queue.length > 0) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const step = steps.find((s) => s.id === id);
    if (step) ordered.push(step);
    const nexts = outgoing.get(id) || [];
    nexts.sort((a, b) => a.localeCompare(b));
    for (const nid of nexts) {
      const deg = (inDegree.get(nid) || 0) - 1;
      inDegree.set(nid, deg);
      if (deg === 0) queue.push(nid);
    }
    queue.sort((a, b) => a.localeCompare(b));
  }

  for (const s of steps) {
    if (!seen.has(s.id)) ordered.push(s);
  }
  return ordered;
}

// 推断列的数据类型
function inferColumnType(columnName: string, data: Record<string, unknown>[]): DataType {
  const values = data.map(r => r[columnName]).filter(v => v !== null && v !== undefined && v !== '');
  if (values.length === 0) return 'string';
  
  // 检查是否都是数字
  if (values.every(v => typeof v === 'number' || !isNaN(Number(v)))) return 'number';
  
  // 检查是否都是日期
  if (values.every(v => /^\d{4}[-/]\d{2}[-/]\d{2}/.test(String(v)))) return 'date';
  
  return 'string';
}

// 从 plan.steps 构建节点（后端已生成完整流程）
function buildNodesFromSteps(plan: Plan, baseY = 100): { nodes: FlowNode[]; edges: FlowEdge[] } {
  const nodes: FlowNode[] = [];
  const edges: FlowEdge[] = [];
  const raw = plan.steps || [];
  if (raw.length === 0) return { nodes, edges };

  const steps = sortPlanStepsForPipeline(raw);

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    const label = STEP_LABELS[step.action] || step.action;
    const params = step.params || {};
    const nodeId = step.id || generateNodeId(step.action);
    const node: FlowNode = {
      id: nodeId,
      type: step.action,
      position: { x: 100, y: baseY + i * 100 },
      data: {
        label,
        config: { type: step.action, params },
        status: 'idle',
      },
    };
    nodes.push(node);
    for (const dep of step.depends || []) {
      edges.push({ id: `e_${dep}_${nodeId}`, source: dep, target: nodeId });
    }
  }
  return { nodes, edges };
}

// 从计划生成表格流节点
function generateNodesFromPlan(plan: Plan, existingNodes: FlowNode[] = [], existingEdges: FlowEdge[] = []): { nodes: FlowNode[]; edges: FlowEdge[] } {
  // 优先使用后端 plan.steps 构建（确保参数正确）
  const steps = plan.steps || [];
  if (steps.length > 0 && existingNodes.length === 0) {
    return buildNodesFromSteps(plan);
  }

  // 修改模式：在现有节点基础上添加
  const nodes: FlowNode[] = [...existingNodes];
  const edges: FlowEdge[] = [...existingEdges];
  let lastNodeId: string | null = null;
  let currentY = 100;

  if (nodes.length > 0) {
    const maxYNode = nodes.reduce((max, node) => node.position.y > max.position.y ? node : max, nodes[0]);
    lastNodeId = maxYNode.id;
    currentY = maxYNode.position.y + 100;
    const exportNodes = nodes.filter(n => n.type === 'export');
    exportNodes.forEach(exportNode => {
      const edgeIndex = edges.findIndex(e => e.target === exportNode.id);
      if (edgeIndex !== -1) {
        lastNodeId = edges[edgeIndex].source;
        edges.splice(edgeIndex, 1);
      }
    });
  } else {
    const loadNode: FlowNode = {
      id: generateNodeId('load'),
      type: 'load_excel',
      position: { x: 100, y: currentY },
      data: {
        label: '',
        config: {
          type: 'load_excel',
          params: { table: plan.intent?.tables?.[0] || 'data' },
        },
        status: 'idle',
      },
    };
    nodes.push(loadNode);
    lastNodeId = loadNode.id;
    currentY += 100;
  }

  const intentType = plan.intent?.type || 'filter';

  switch (intentType) {
    case 'filter':
      const filterNode: FlowNode = {
        id: generateNodeId('filter'),
        type: 'filter',
        position: { x: 100, y: currentY },
        data: {
          label: '',
          config: {
            type: 'filter',
            params: { 
              conditions: plan.intent?.conditions || [],
            },
          },
          status: 'idle',
        },
      };
      nodes.push(filterNode);
      if (lastNodeId) {
        edges.push({ id: `e_${lastNodeId}_${filterNode.id}`, source: lastNodeId, target: filterNode.id });
      }
      lastNodeId = filterNode.id;
      currentY += 100;
      break;
      
    case 'aggregate':
      const aggNode: FlowNode = {
        id: generateNodeId('aggregate'),
        type: 'aggregate',
        position: { x: 100, y: currentY },
        data: {
          label: '',
          config: {
            type: 'aggregate',
            params: {
              groupBy: plan.intent?.columns || [],
              aggregations: [{ column: 'amount', function: 'sum' }],
            },
          },
          status: 'idle',
        },
      };
      nodes.push(aggNode);
      if (lastNodeId) {
        edges.push({ id: `e_${lastNodeId}_${aggNode.id}`, source: lastNodeId, target: aggNode.id });
      }
      lastNodeId = aggNode.id;
      currentY += 100;
      break;
      
    case 'sort':
      const sortNode: FlowNode = {
        id: generateNodeId('sort'),
        type: 'sort',
        position: { x: 100, y: currentY },
        data: {
          label: '',
          config: {
            type: 'sort',
            params: {
              fields: (plan.intent?.columns || []).map((col: string) => ({
                column: col,
                order: 'asc',
              })),
            },
          },
          status: 'idle',
        },
      };
      nodes.push(sortNode);
      if (lastNodeId) {
        edges.push({ id: `e_${lastNodeId}_${sortNode.id}`, source: lastNodeId, target: sortNode.id });
      }
      lastNodeId = sortNode.id;
      currentY += 100;
      break;
      
    case 'join':
      const joinNode: FlowNode = {
        id: generateNodeId('join'),
        type: 'join',
        position: { x: 100, y: currentY },
        data: {
          label: '',
          config: {
            type: 'join',
            params: {
              joinType: 'left',
              keyColumns: plan.intent?.columns || [],
            },
          },
          status: 'idle',
        },
      };
      nodes.push(joinNode);
      if (lastNodeId) {
        edges.push({ id: `e_${lastNodeId}_${joinNode.id}`, source: lastNodeId, target: joinNode.id });
      }
      lastNodeId = joinNode.id;
      currentY += 100;
      break;

    case 'dedup':
      const dedupNode: FlowNode = {
        id: generateNodeId('dedup'),
        type: 'dedup',
        position: { x: 100, y: currentY },
        data: {
          label: '',
          config: {
            type: 'dedup',
            params: {
              columns: plan.intent?.columns || [],
            },
          },
          status: 'idle',
        },
      };
      nodes.push(dedupNode);
      if (lastNodeId) {
        edges.push({ id: `e_${lastNodeId}_${dedupNode.id}`, source: lastNodeId, target: dedupNode.id });
      }
      lastNodeId = dedupNode.id;
      currentY += 100;
      break;

    case 'compute':
      const computeNode: FlowNode = {
        id: generateNodeId('compute'),
        type: 'compute',
        position: { x: 100, y: currentY },
        data: {
          label: '',
          config: {
            type: 'compute',
            params: {
              expressions: plan.intent?.entities?.expressions || [],
            },
          },
          status: 'idle',
        },
      };
      nodes.push(computeNode);
      if (lastNodeId) {
        edges.push({ id: `e_${lastNodeId}_${computeNode.id}`, source: lastNodeId, target: computeNode.id });
      }
      lastNodeId = computeNode.id;
      currentY += 100;
      break;

    case 'group_by':
      const groupByNode: FlowNode = {
        id: generateNodeId('group_by'),
        type: 'group_by',
        position: { x: 100, y: currentY },
        data: {
          label: '',
          config: {
            type: 'group_by',
            params: {
              groupBy: plan.intent?.columns || [],
            },
          },
          status: 'idle',
        },
      };
      nodes.push(groupByNode);
      if (lastNodeId) {
        edges.push({ id: `e_${lastNodeId}_${groupByNode.id}`, source: lastNodeId, target: groupByNode.id });
      }
      lastNodeId = groupByNode.id;
      currentY += 100;
      break;
  }
  
  // 如果没有导出节点，添加一个
  const hasExportNode = nodes.some(n => n.type === 'export');
  if (!hasExportNode && lastNodeId) {
    const exportNode: FlowNode = {
      id: generateNodeId('export'),
      type: 'export',
      position: { x: 100, y: currentY },
      data: {
        label: '',
        config: {
          type: 'export',
          params: {},
        },
        status: 'idle',
      },
    };
    nodes.push(exportNode);
    edges.push({ id: `e_${lastNodeId}_${exportNode.id}`, source: lastNodeId, target: exportNode.id });
  } else if (hasExportNode && lastNodeId) {
    // 重新连接导出节点到最后一个处理节点
    const exportNode = nodes.find(n => n.type === 'export')!;
    edges.push({ id: `e_${lastNodeId}_${exportNode.id}`, source: lastNodeId, target: exportNode.id });
  }
  
  return { nodes, edges };
}

interface AIState {
  // 当前意图
  currentIntent: Intent | null;
  
  // 当前计划
  currentPlan: Plan | null;
  
  // 生成的 Workflow JSON
  generatedWorkflow: string | null;
  
  // 生成的节点和边
  generatedNodes: FlowNode[];
  generatedEdges: FlowEdge[];
  
  // 流式构建过程（类似 Cursor 构建日志）
  buildSteps: BuildProgress[];
  
  // 加载状态
  isParsing: boolean;
  isGenerating: boolean;
  
  // 错误信息
  error: string | null;
  
  // 操作
  setCurrentIntent: (intent: Intent | null) => void;
  setCurrentPlan: (plan: Plan | null) => void;
  setGeneratedWorkflow: (workflow: string | null) => void;
  parseIntent: (input: string, context?: { availableTables: { name: string; columns: { name: string; type: string }[] }[] }) => Promise<void>;
  /** 流式构建工作流：三阶段 Prompt 串联，每步进度推送到 buildSteps，最后得到完整 plan/workflow */
  parseIntentStream: (input: string, context?: BuildStreamContext) => Promise<void>;
  generatePlan: (intent: Intent, tables?: { name: string; columns: { name: string; type: string }[] }[]) => Promise<void>;
  confirmPlan: (planId: string, confirm: boolean, feedback?: string) => Promise<void>;
  applyGeneratedWorkflow: (uploadedFileOrList?: UploadedFileData | UploadedFileData[] | null) => void;
  modifyWorkflow: (input: string, context?: BuildStreamContext, uploadedFile?: UploadedFileData | null) => Promise<void>;
  clear: () => void;
  reset: () => void;
}

export const useAIStore = create<AIState>()((set, get) => ({
  currentIntent: null,
  currentPlan: null,
  generatedWorkflow: null,
  generatedNodes: [],
  generatedEdges: [],
  buildSteps: [],
  isParsing: false,
  isGenerating: false,
  error: null,

  setCurrentIntent: (intent) => set({ currentIntent: intent }),
  
  setCurrentPlan: (plan) => set({ currentPlan: plan }),
  
  setGeneratedWorkflow: (workflow) => set({ generatedWorkflow: workflow }),

  parseIntent: async (input, context) => {
    set({ isParsing: true, error: null });
    try {
      const response = await aiApi.parseIntent(input, context);
      if (response.code === 0) {
        const plan = response.data.plan || null;
        
        // 生成节点和边
        let nodes: FlowNode[] = [];
        let edges: FlowEdge[] = [];
        
        if (plan) {
          const generated = generateNodesFromPlan(plan);
          nodes = generated.nodes;
          edges = generated.edges;
        }
        
        set({
          currentIntent: response.data.intent,
          currentPlan: plan,
          generatedWorkflow: response.data.workflow || null,
          generatedNodes: nodes,
          generatedEdges: edges,
        });
      } else {
        set({ error: response.message });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      set({ isParsing: false });
    }
  },

  parseIntentStream: async (input, context) => {
    set({ isParsing: true, error: null, buildSteps: [] });
    try {
      await aiApi.buildWorkflowStream(
        input,
        context,
        {
          onProgress: (p) => {
            set((s) => ({ buildSteps: [...s.buildSteps, p] }));
          },
          onDone: (data) => {
            const plan = data.plan;
            let nodes: FlowNode[] = [];
            let edges: FlowEdge[] = [];
            if (plan?.steps?.length) {
              const generated = buildNodesFromSteps(plan);
              nodes = generated.nodes;
              edges = generated.edges;
            }
            set({
              currentIntent: data.intent,
              currentPlan: plan,
              generatedWorkflow: data.workflow ?? null,
              generatedNodes: nodes,
              generatedEdges: edges,
            });
          },
          onError: (message) => set({ error: message }),
        }
      );
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      set({ isParsing: false });
    }
  },

  generatePlan: async (intent, tables) => {
    set({ isGenerating: true, error: null });
    try {
      const response = await aiApi.generatePlan(intent, tables);
      if (response.code === 0) {
        const plan = response.data.plan;
        
        // 生成节点和边
        let nodes: FlowNode[] = [];
        let edges: FlowEdge[] = [];
        
        if (plan) {
          const generated = generateNodesFromPlan(plan);
          nodes = generated.nodes;
          edges = generated.edges;
        }
        
        set({
          currentPlan: plan,
          generatedWorkflow: response.data.workflow,
          generatedNodes: nodes,
          generatedEdges: edges,
        });
      } else {
        set({ error: response.message });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      set({ isGenerating: false });
    }
  },

  confirmPlan: async (planId, confirm, feedback) => {
    try {
      const response = await aiApi.confirmPlan(planId, confirm, feedback);
      if (response.code === 0) {
        // 处理确认结果
        if (confirm && response.data.workflowId) {
          // 创建成功，可以跳转到表格流编辑器
        }
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
    }
  },
  
  // 应用生成的表格流到 workflow store。支持多文件：uploadedFiles 与多个 load 节点按表名或顺序一一对应。
  applyGeneratedWorkflow: (uploadedFileOrList?: UploadedFileData | UploadedFileData[] | null) => {
    const { generatedNodes, generatedEdges } = get();
    if (generatedNodes.length === 0) return;

    const uploadedFileStore = useUploadedFileStore.getState();
    const workflowStore = useWorkflowStore.getState();
    workflowStore.clearWorkflow();

    const fileList: UploadedFileData[] = Array.isArray(uploadedFileOrList)
      ? uploadedFileOrList
      : uploadedFileOrList
        ? [uploadedFileOrList]
        : [];

    const tableNameFromFile = (f: UploadedFileData) => f.name.replace(/\.[^/.]+$/, '') || f.name;
    const loadNodes = generatedNodes.filter(
      (n) => n.type === 'load_excel' || n.type === 'load_csv' || n.type === 'load_table'
    );

    const processedNodes = generatedNodes.map((node) => {
      if (node.type !== 'load_excel' && node.type !== 'load_csv' && node.type !== 'load_table') return node;

      const tableInNode = node.data?.config?.params?.table as string | undefined;
      let file: UploadedFileData | undefined;
      const idx = loadNodes.indexOf(node);
      if (fileList.length > 0) {
        file =
          fileList.find((f) => tableNameFromFile(f) === tableInNode) ??
          fileList[idx] ??
          fileList[0];
      }

      if (!file) return node;

      uploadedFileStore.setFileForNode(node.id, file);
      const columns: Column[] = file.columns.map((name, i) => ({
        name,
        type: inferColumnType(name, file!.data),
        nullable: file!.data.some(
          (r: Record<string, unknown>) =>
            r[name] === null || r[name] === undefined || r[name] === ''
        ),
        index: i,
      }));

      return {
        ...node,
        data: {
          ...node.data,
          config: {
            type: node.type,
            params: {
              ...node.data.config.params,
              fileName: file.name,
              rowCount: file.rowCount,
              table: tableInNode || tableNameFromFile(file),
              columns,
            },
          },
        },
      };
    });

    processedNodes.forEach((node) => workflowStore.addNode(node));
    generatedEdges.forEach((edge) => workflowStore.addEdge(edge));
    set({ generatedNodes: processedNodes });
  },

  // 修改现有表格流 - 使用与生成相同的 SSE 流式接口，展示构建过程，并在现有节点基础上合并新步骤
  modifyWorkflow: async (input, context) => {
    set({ isParsing: true, error: null, buildSteps: [] });
    try {
      const existingNodes = get().generatedNodes;
      const existingEdges = get().generatedEdges;

      await aiApi.buildWorkflowStream(
        input,
        context,
        {
          onProgress: (p) => {
            set((s) => ({ buildSteps: [...s.buildSteps, p] }));
          },
          onDone: (data) => {
            const plan = data.plan;
            let nodes: FlowNode[] = [];
            let edges: FlowEdge[] = [];
            if (plan) {
              // 在现有节点基础上合并新计划（与原先 modify 行为一致）
              const generated = generateNodesFromPlan(plan, existingNodes, existingEdges);
              nodes = generated.nodes;
              edges = generated.edges;
            }
            set({
              currentIntent: data.intent,
              currentPlan: plan,
              generatedWorkflow: data.workflow ?? null,
              generatedNodes: nodes,
              generatedEdges: edges,
            });
          },
          onError: (message) => set({ error: message }),
        }
      );
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      set({ isParsing: false });
    }
  },

  clear: () =>
    set({
      currentIntent: null,
      currentPlan: null,
      generatedWorkflow: null,
      buildSteps: [],
      error: null,
    }),

  reset: () => {
    resetNodeIdCounter();
    set({
      currentIntent: null,
      currentPlan: null,
      generatedWorkflow: null,
      generatedNodes: [],
      generatedEdges: [],
      buildSteps: [],
      error: null,
    });
  },
}));
